import React, { useState, useEffect } from 'react';
import { AuthorizationError } from '@lf/api-client/lfApi';
import { LoadingButton, ToggleButton } from '@lf/ui-primitives';
import FreeTrialStart from './FreeTrialStart';
import { getSubscriptionAction } from '../../api/lfApiSubscription';

const AddChildProfile = ({ childProfile, showConsent, onOk, onCancel }) => {
    const [childProfileEdited, setChildProfileEdited] = useState({
        ...(childProfile || {
            first_name: '',
            grade_level: '',
            username: '',
            password: '',
        }),
        parent_consent: showConsent ? 1 : 0,
        collect_usage_data: 1,
    });
    const [isParentConsent, setIsParentConsent] = useState(!showConsent);
    const [subscriptionAction, setSubscriptionAction] = useState({action: 'none'});
    const [errorType, setErrorType] = useState('');

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setChildProfileEdited(prevState => ({ ...prevState, [name]: value }));
    };

    const isSaveEnabled = () => {
        if (!childProfileEdited.first_name) {
            return false;
        }
        if (!childProfileEdited.grade_level) {
            return false;
        }
        if (!childProfile && !childProfileEdited.username) {
            return false;
        }
        if (!childProfile && !childProfileEdited.password) {
            return false;
        }
        if (!isParentConsent) {
            return false;
        }
        // Flat-tier pricing bills at quantity=1, so adding a child never
        // changes the price — the only blocker is an unpaid paywall.
        if (['create_new', 'create_new_has_canceled'].includes(subscriptionAction.action)) {
            return false;
        }
        return true;
    }

    const handleSave = async () => {
        setErrorType('');
        // Save is only enabled when no subscription action is pending
        // (active sub or active app trial), so just persist the child.
        try {
            await onOk(childProfileEdited);
        } catch (error) {
            if (error instanceof AuthorizationError) {
                setErrorType('username_already_exists');
                setChildProfileEdited(prevState => ({ ...prevState, username: '' }));
            } else {
                console.error('Error in handleSave:', error.message);
            }
        }
    };

    useEffect(() => {
        setSubscriptionAction({action: 'none'});
        if (!childProfile) {
            getSubscriptionAction(true).then(action => {
                if (action) {
                    setSubscriptionAction(action);
                }
            });
        }
    }, [childProfile]);

    return <div className="text-left flex justify-center">
        <div>
            <h1 className="mb-4 text-lg">
                {childProfile ? "Edit child profile" : "Add a new child profile"}
            </h1>

            {
                ['create_new', 'create_new_has_canceled'].includes(subscriptionAction.action) &&
                <FreeTrialStart subscriptionAction={subscriptionAction} />
            }
            {
                errorType === 'username_already_exists' && <div className="border border-red-500 border-2 p-4 mb-8 md:w-3/4">
                    <div className="font-semibold">Username already exists</div>
                    <div>Please use a different username.</div>
                </div>
            }

            <div className="grid grid-cols-[auto,1fr] gap-2 items-center text-left mb-2">
                <div className="text-right">Display Name: </div>
                <div>
                    <input
                        type="text"
                        className="border border-gray-300 p-1"
                        name="first_name"
                        value={childProfileEdited.first_name}
                        onChange={handleProfileChange} />
                </div>
                <div className="text-right">Grade Level: </div>
                <div>
                    <input
                        type="text"
                        className="border border-gray-300 p-1"
                        name="grade_level"
                        value={childProfileEdited.grade_level}
                        onChange={handleProfileChange} />
                </div>
                { !childProfile && <div className="text-right">Username: </div> }
                { 
                    !childProfile && <div>
                        <input
                            type="text"
                            className="border border-gray-300 p-1"
                            name="username"
                            value={childProfileEdited.username}
                            onChange={handleProfileChange} />
                    </div> 
                }
                { !childProfile && <div className="text-right">Password: </div> }
                { 
                    !childProfile && <div>
                        <input
                            type="password"
                            className="border border-gray-300 p-1"
                            name="password"
                            value={childProfileEdited.password}
                            onChange={handleProfileChange} />
                    </div>
                }
            </div>

            {
                showConsent && <div className="py-4">
                    <div className="py-1 flex items-center">
                        <ToggleButton isToggled={isParentConsent} setIsToggled={setIsParentConsent} />
                        <span className={`${isParentConsent ? '' : 'text-gray-500'} ml-2 md:w-1/2`}>
                        I am the parent or legal guardian and I consent to the collection and use of my child's information
                        in accordance with the <a href="https://learnforest.app/privacy.html" className="underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                        </span>
                    </div>
                    <div className="py-1 flex items-center">
                        <ToggleButton isToggled={childProfileEdited.collect_usage_data && isParentConsent} setIsToggled={v => setChildProfileEdited(prevState => ({ ...prevState, collect_usage_data: v ? 1 : 0 }))} />
                        <span className={`${childProfileEdited.collect_usage_data && isParentConsent ? '' : 'text-gray-500'} ml-2 md:w-1/2`}>
                        I consent to the collection and use of app usage data to improve the learning experience.
                        </span>
                    </div>
                </div>
            }

            <div className="mt-4 flex gap-2">
                <LoadingButton text={ childProfile ? "Save": "Next" } onClick={handleSave} disabled={!isSaveEnabled()} />
                <button className="bg-blue-500 text-white py-2 px-4 rounded"
                    onClick={onCancel}>
                    Cancel
                </button>
            </div>
        </div>
    </div>;
};

export default AddChildProfile;
